import { PeopleOutlined } from '@mui/icons-material';
import {
  Badge,
  Box,
  Checkbox,
  ClickAwayListener,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebounce } from '../hooks/useDebounce';
import { useFetchData } from '../hooks/useFetchData';
import { StyledIconButton } from '../theme/CustomComponents';
import { User } from '../Types/user';
import { usersPathUrl } from '../util/routesUrls';
import { useAuth } from './Providers/AuthProvider';

interface ShareButtonProps {
  sharedWith: Record<string, string>;
  onSave: (selectedUsers: string[]) => void;
  ignoreIds?: string[];
}

const ShareButton: React.FC<ShareButtonProps> = ({ sharedWith, onSave, ignoreIds }) => {
  const { currentUser } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const popperOpen = Boolean(anchorEl);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { data: users } = useFetchData<User[]>({
    urlPath: usersPathUrl(selectedUsers, debouncedSearch),
    skip: !popperOpen,
  });

  const validUsers = useMemo(() => {
    if (!users || !currentUser) return [];

    return users.filter((user) => {
      const isNotCurrentUser = user.id !== currentUser.id;
      const isNotIgnored = !ignoreIds?.includes(user.id);
      return isNotCurrentUser && isNotIgnored;
    });
  }, [users, currentUser, ignoreIds]);

  const validSelectedUsers = useMemo(
    () => selectedUsers?.filter((userId) => userId !== currentUser?.id),
    [selectedUsers, currentUser]
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl]
  );

  const handleUserSelect = useCallback(
    (userId: string) => {
      const newSelectedUsers = selectedUsers.includes(userId)
        ? selectedUsers.filter((id) => id !== userId)
        : [...selectedUsers, userId];

      setSelectedUsers(newSelectedUsers);
      onSave(newSelectedUsers);
    },
    [selectedUsers, onSave]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearchTerm('');
  }, []);

  useEffect(() => {
    setSelectedUsers(Object.keys(sharedWith ?? {}));
  }, [sharedWith]);

  return (
    <>
      <Tooltip
        title={
          validSelectedUsers.length === 0
            ? 'Collaborate'
            : validSelectedUsers.length === 1
            ? 'Collaborating with 1 User'
            : `Collaborating with ${validSelectedUsers.length} Users`
        }
        arrow
      >
        <StyledIconButton onClick={handleClick}>
          <Badge
            badgeContent={validSelectedUsers.length}
            color="primary"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            overlap="circular"
          >
            <PeopleOutlined />
          </Badge>
        </StyledIconButton>
      </Tooltip>

      <Popper
        open={popperOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 999 }}
        onClick={(e) => e.stopPropagation()}
        disablePortal
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper sx={{ mt: 1, width: 280, maxHeight: 300, overflowY: 'auto' }}>
            <Box px={2} py={1}>
              <TextField
                inputRef={inputRef}
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                autoFocus
              />
            </Box>
            {validUsers?.length === 0 && debouncedSearch ? (
              <MenuItem disabled>No users found</MenuItem>
            ) : (
              validUsers?.map((user) => (
                <MenuItem
                  key={user.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleUserSelect(user.id);
                  }}
                >
                  <Checkbox checked={validSelectedUsers.includes(user.id)} />
                  <ListItemText primary={user.preferredName} />
                </MenuItem>
              ))
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default ShareButton;
